import {
  type PropsWithChildren,
  type RefObject,
  type UIEvent,
  useEffect,
  useRef,
  useState,
} from "react"

import { theme } from "../../theme"
import { AnimatedElement } from "../AnimatedElement"
import { Box } from "../Box"
import { IconButton } from "../IconButton"
import styles from "./ScrollArea.module.css"

interface ScrollAreaControlsProps extends PropsWithChildren {
  /**
   * A reference to the scroll area component.
   */
  scrollAreaRef: RefObject<HTMLDivElement>
  direction?: "vertical" | "horizontal"
}

const scrollbarSize = theme.size.scrollbar

function references(direction: "vertical" | "horizontal") {
  const isVertical = direction === "vertical"

  return {
    icon: {
      start: isVertical ? "expandLess" : "chevronLeft",
      end: isVertical ? "expandMore" : "chevronRight",
    },
    placement: {
      start: {
        top: 0,
        left: 0,
        right: isVertical ? scrollbarSize : undefined,
        bottom: isVertical ? undefined : scrollbarSize,
      },
      end: {
        bottom: isVertical ? 0 : scrollbarSize,
        right: isVertical ? scrollbarSize : 0,
        top: isVertical ? undefined : 0,
        left: isVertical ? 0 : undefined,
      },
    },
    clientSize: isVertical ? "clientHeight" : "clientWidth",
    scrollSize: isVertical ? "scrollHeight" : "scrollWidth",
    scrollPosition: isVertical ? "scrollTop" : "scrollLeft",
  } as const
}

/**
 * @private
 * This is an internal component we will always use this within the context of a ScrollArea component.
 */
export function ScrollAreaControls(props: ScrollAreaControlsProps) {
  const { children, scrollAreaRef, direction = "vertical" } = props
  const { clientSize, scrollPosition, scrollSize, icon, placement } =
    references(direction)

  const [showStart, setShowStart] = useState(false)
  const [showEnd, setShowEnd] = useState(false)
  const isMoving = useRef(false)

  function setIsMoving(_isMoving: boolean) {
    isMoving.current = _isMoving
  }

  useEffect(() => {
    if (!scrollAreaRef.current) return

    const scrollArea = scrollAreaRef.current

    // determine whether content is scrollable and that the scrolled element is not at the end of the scrollable container
    // if true show the end button to allow and indicate that it is possible to scroll forward
    const scrollAreaIsLargerThanContainer =
      scrollArea[scrollSize] > scrollArea[clientSize]
    const scrollIsAtEnd =
      scrollArea[scrollPosition] >=
      scrollArea[scrollSize] - scrollArea[clientSize]
    setShowEnd(scrollAreaIsLargerThanContainer && !scrollIsAtEnd)

    // determine whether content has been scrolled away from start position
    // if true show the start button to allow and indicate that it is possible to scroll backward
    const scrollAreaIsNotInStartPosition = scrollArea[scrollPosition] > 0
    setShowStart(scrollAreaIsNotInStartPosition)
  }, [scrollAreaRef, children, clientSize, scrollPosition, scrollSize])

  function move(direction: "start" | "end") {
    // continuously move the scroll position while the isMoving reference is true
    if (isMoving.current) {
      scroll(direction)
      requestAnimationFrame(() => move(direction))
    }
  }

  function scroll(direction: "start" | "end") {
    if (!scrollAreaRef.current) return

    const element = scrollAreaRef.current
    // the pixel amount to move the container per iteration
    const scrollAmount = 2

    switch (direction) {
      case "start":
        element[scrollPosition] -= scrollAmount
        break
      case "end":
        element[scrollPosition] += scrollAmount
        break
    }
  }

  function onScroll(event: UIEvent<HTMLDivElement>) {
    // Since this should only be used in the context of ScrollArea
    // we assume that there will only be one child and it will be scrollable
    const element = event.currentTarget.children[0]
    if (!element) return

    const currentScrollPosition = element[scrollPosition]
    const isScrolled = currentScrollPosition > 0
    const hasReachedEnd =
      currentScrollPosition >= element[scrollSize] - element[clientSize]

    setShowStart(isScrolled)
    setShowEnd(!hasReachedEnd)

    // reset the isMoving ref when buttons are removed
    // if it isn't the animation request will continue to run
    // and there will be no button to stop the requests
    if (!isScrolled || hasReachedEnd) setIsMoving(false)
  }

  return (
    <Box
      overflow="hidden"
      position="relative"
      width="100%"
      flexDirection={direction === "vertical" ? "row" : "column"}
    >
      <div
        onScrollCapture={onScroll}
        style={{
          width: "100%",
          overflowX: "auto",
        }}
      >
        {children}
      </div>
      <AnimatedElement animation={{ name: "fade" }}>
        {showStart && (
          <Box
            position="absolute"
            backgroundColor="grey2"
            alignItems="center"
            {...placement.start}
          >
            <div className={styles.gradient}>
              <IconButton
                icon={icon.start}
                onMouseDown={() => {
                  setIsMoving(true)
                  move("start")
                }}
                onMouseUp={() => setIsMoving(false)}
              />
            </div>
          </Box>
        )}
      </AnimatedElement>
      <AnimatedElement animation={{ name: "fade" }}>
        {showEnd && (
          <Box
            position="absolute"
            backgroundColor="grey2"
            alignItems="center"
            {...placement.end}
          >
            <div className={styles.gradient}>
              <IconButton
                icon={icon.end}
                onMouseDown={() => {
                  setIsMoving(true)
                  move("end")
                }}
                onMouseUp={() => setIsMoving(false)}
              />
            </div>
          </Box>
        )}
      </AnimatedElement>
    </Box>
  )
}
